import dayjs from "dayjs"
import { flatten, unflatten } from "flat"
import { ExtractPropTypes } from "vue"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import { QueryResult } from "@/api/utils/types"
import {
  mapValues,
  TFunction,
  IGlobalGetter,
  IFlowConfigInput,
  wrapInCurly,
  IFlowDataAccessConfigGetters,
  isObject,
} from "../../utils"

type GetterConfig = IFlowConfigInput & IGlobalGetter
type GetterConfigMap = Record<string, GetterConfig>

// Helper recursively mapping values types types from 'source' type into 'target' type
type MapValuesTo<T, TTargetType, TSourceType> = {
  [K in keyof T]-?: T[K] extends TSourceType
    ? TTargetType
    : T[K] extends object
      ? MapValuesTo<T[K], TTargetType, TSourceType>
      : TTargetType
}

/**
 * Helper extracting return type from a getter object
 * It attempts to extract it from the following properties and returns the first available:
 *  1. empty() function
 *  2. getter() function
 *  3. 'type' property
 */
type ExtractGetterReturnType<
  T,
  D extends { type?: any } = ExtractPropTypes<{ type: T }>,
> = T extends {
  empty: () => infer X
}
  ? X
  : T extends {
        getter: () => QueryResult<infer Y>
      }
    ? Y
    : T extends {
          type: any
        }
      ? D["type"]
      : never

type ExpandedObject<T> = { [K in keyof T]: ExtractGetterReturnType<T[K]> }
type ExpandedObjectMap<T> = { [K in keyof T]: ExpandedObject<T[K]> }
type ExtractGetterReturnTypes<T> = {
  [K in keyof T]: ExtractReturnedQueryResultType<T[K]>
}
type ExtractReturnedQueryResultType<T> = T extends () => AggregatedQueryProps<
  infer Z
>
  ? Z
  : never

const isParentProperty = (value: unknown): value is object =>
  isObject(value) && !(value instanceof Date) && !(value instanceof dayjs)

/**
 * Function taking getter map and returning a cloned map populated with
 * results of 'empty' function associated with each getter, and populating it with the getter function.
 *
 * Example input:
 * {
 *    current: { account: { empty: () => { new Account() }, getter: Function }}
 * }
 *
 * Example output:
 * {
 *    current: { account: { firstName: Function, address: { postcode: Function, addressLine1: Function } }}
 * }
 *
 * This is useful to construct a typed object that can be used in flow schema, e.g. Getters.current.account.address.postcode
 */
export const injectEmptyObjects = <T>(object: T): ExpandedObject<T> => {
  const assignGetterToAllProps = <T, X>(
    value: T,
    getter: X,
  ): X | MapValuesTo<T, X, GetterConfig> => {
    if (isParentProperty(value)) {
      return mapValues(value, (nestedValue: T[keyof T]) =>
        assignGetterToAllProps(nestedValue, getter),
      ) as MapValuesTo<T, X, GetterConfig>
    }
    return getter
  }

  return mapValues(object as GetterConfigMap, (v) => {
    // Getters that do not provide 'empty' function are not expanded
    return !(v.empty instanceof Function)
      ? v.getter
      : mapValues(v.empty(), (nestedValue) =>
          assignGetterToAllProps(nestedValue, v.getter),
        )
  }) as ExpandedObject<T>
}

export const injectEmptyObjectsIntoChildProps = <T>(
  object: T,
): ExpandedObjectMap<T> =>
  mapValues(
    object as GetterConfigMap,
    injectEmptyObjects,
  ) as ExpandedObjectMap<T>

export const extractGetters = (object: IFlowDataAccessConfigGetters) =>
  mapValues(object, (getterMap) =>
    mapValues(getterMap, (getterConfig) => getterConfig.getter),
  )

export const serializeGetters = <T extends Record<string, any>>(api: T): T => {
  const flatApi = flatten(api)
  const flatApiKeyToKeyMap = mapValues(flatApi, (value, key) =>
    wrapInCurly(key),
  )
  return unflatten(flatApiKeyToKeyMap) as T
}

export const extractQueryTypes = <T>(getters: T): ExtractGetterReturnTypes<T> =>
  getters as ExtractGetterReturnTypes<T>

export const serializeActions = <T extends Record<string, any>>(
  api: T,
): MapValuesTo<T, string, TFunction> => {
  const flatApi = flatten(api)
  const flatApiKeyToKeyMap = mapValues(flatApi, (value, key) => key)
  // Manual casting required as unflatten function is not typed
  return unflatten(flatApiKeyToKeyMap) as MapValuesTo<T, string, TFunction>
}
